import { InfoTip } from '../ui/InfoTip'
import { widgetHelp } from '../../content/widgets'
import { Link } from 'react-router-dom'
import { namespaceTag, scopeQuery, type ScopeChoice } from '../provider/scopeChoice'
import { MessageTable } from './MessageTable'
import { useDeadLetters } from '../../hooks/useDeadLetters'
import type { CloudProvider, Namespace } from '../../lib/api/namespaces'

/**
 * Home's "Latest dead letters": the dead-letter table in compact form, five rows. Each row's *Open →*
 * lands on the Dead letters view with that message selected. Home says what is going on; Dead letters
 * is where you act — so there is one table, drawn twice, and no second implementation.
 * Draws nothing when there is nothing to show: an empty box is not information.
 */
export function RecentDeadLetters({ provider, namespaces, choice, recorded = false }: { provider: CloudProvider; namespaces: readonly Namespace[]; choice: ScopeChoice; recorded?: boolean }) {
  const { data, isError, refetch } = useDeadLetters({ provider, namespaceId: choice.ns?.id, environment: choice.env ?? undefined, status: 'active', page: 1, pageSize: 5 })
  if (isError) return <p role="alert" className="text-sm">ServiceHub couldn’t read the latest dead letters. <button type="button" onClick={() => void refetch()} className="font-medium text-[var(--color-primary-700)] hover:underline">Try again</button></p>
  if (!data || data.items.length === 0) return null

  return (
    <section aria-label={recorded ? 'Latest recorded dead letters' : 'Latest dead letters'} className="h-full rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)]">
      <div className="flex items-center justify-between px-4 py-3">
        <h2 className="flex items-center text-sm font-semibold text-[var(--color-text)]">{recorded ? 'Latest recorded dead letters' : 'Latest dead letters'}<InfoTip help={widgetHelp.latest} /></h2>
        <Link to={`/?tab=dlq${scopeQuery(choice)}`} className="text-sm font-medium text-[var(--color-primary-700)] hover:underline">
          See all {data.paging.total.toLocaleString()} →
        </Link>
      </div>
      <MessageTable
        rows={data.items}
        namespaceNames={new Map(namespaces.map((n) => [n.id, namespaceTag(n)]))}
        compact
        glance
        caption="The five most recent dead-lettered messages"
      />
    </section>
  )
}
